import { useMyMemberships } from '../gym/useMyMemberships'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router'
import { Button } from '@structural-climbing/ui'
import { profileFixLink, type ProfileFixField } from '../profile/profileFixLink'

/**
 * The one thing standing between a member and entering: their profile is
 * missing the gender (or date of birth) the categories are split on.
 *
 * Said up front and loud, with a button that goes straight to that field and
 * brings them back here afterwards. A faded line under each disabled category
 * was easy to read as "this competition is not for you".
 */
export function ProfileNeededCallout({ field }: { field: ProfileFixField }) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const key = field === 'gender' ? 'Gender' : 'Dob'
  // A child's profile is the parent's to fill in, so there is nowhere to send them.
  const isChildAccount = useMyMemberships().childAccount !== null

  return (
    <div
      role="alert"
      className="flex flex-col gap-4 rounded-2xl border border-primary/30 bg-primary/5 p-5 shadow-sm sm:flex-row sm:items-center"
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-6 w-6"
          aria-hidden="true"
        >
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21a8 8 0 0 1 16 0" />
        </svg>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-base font-bold">
          {isChildAccount
            ? t('competitions.childDetailsTitle')
            : t(`competitions.profileNeeds${key}Title`)}
        </p>
        <p className="text-sm text-foreground/70">
          {isChildAccount
            ? t('competitions.childDetailsBody')
            : t(`competitions.profileNeeds${key}Body`)}
        </p>
      </div>

      {!isChildAccount && (
        <Button
          as={Link}
          to={profileFixLink(field, pathname)}
          color="primary"
          size="lg"
          className="shrink-0 font-semibold"
          endContent={<span aria-hidden="true">→</span>}
        >
          {t(`competitions.profileNeeds${key}Cta`)}
        </Button>
      )}
    </div>
  )
}
